import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Stethoscope, LifeBuoy, CheckCircle2, ArrowLeft, Send, Loader2,
  Paperclip, X as XIcon, FileText, ImageIcon as ImgIcon,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/i18n/I18nContext";
import LocaleThemeSwitch from "@/components/common/LocaleThemeSwitch";

const ISSUE_TYPES = [
  { value: "clinical", label: "Clinical" },
  { value: "technical", label: "Technical" },
  { value: "billing", label: "Billing" },
  { value: "access_login", label: "Access & Login" },
  { value: "feedback", label: "Feedback" },
  { value: "other", label: "Other" },
];

// Attachment constraints — keep in sync with backend routers/support.py
const MAX_FILES = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB
const ALLOWED_EXT = ["pdf", "jpg", "jpeg", "png"];
const ALLOWED_MIME = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];

function humanSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileExt(name) {
  const i = (name || "").lastIndexOf(".");
  return i >= 0 ? name.slice(i + 1).toLowerCase() : "";
}

function fileIcon(f) {
  return (f.type || "").startsWith("image/") ? ImgIcon : FileText;
}

/**
 * Reusable Support form + confirmation.
 * - `variant="public"` renders with a full-page brand chrome (used from Login).
 * - `variant="app"`   renders as a standard inner page (used inside AppLayout).
 */
export default function Support({ variant = "app" }) {
  const t = useT();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    subject: "",
    issue_type: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState(null); // { ticket_number, ... }
  const [files, setFiles] = useState([]); // [{ id, file: File }]
  const fileInputRef = useRef(null);

  // Prefill first 3 fields when a user is signed in.
  useEffect(() => {
    if (!user) return;
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
    // Avoid double-prepending the dial code — some seed users already store
    // the phone with a leading '+' or with the country prefix inline.
    const rawPhone = (user.phone || "").trim();
    let mobile = rawPhone;
    if (rawPhone && !rawPhone.startsWith("+") && user.dial_code) {
      const dc = String(user.dial_code).trim();
      const dcDigits = dc.replace(/\D/g, "");
      if (!rawPhone.startsWith(dc) && !rawPhone.startsWith(dcDigits)) {
        mobile = `${dc} ${rawPhone}`;
      }
    }
    setForm((f) => ({
      ...f,
      name: fullName || f.name,
      mobile: mobile || f.mobile,
      email: user.email || f.email,
    }));
  }, [user]);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function addFiles(list) {
    setError("");
    if (!list || !list.length) return;
    const incoming = Array.from(list);
    const remainingSlots = MAX_FILES - files.length;
    if (incoming.length > remainingSlots) {
      setError(`You can attach up to ${MAX_FILES} files. Please remove some to add more.`);
      return;
    }
    const accepted = [];
    for (const file of incoming) {
      const ext = fileExt(file.name);
      const mimeOk = ALLOWED_MIME.includes(file.type) || (!file.type && ALLOWED_EXT.includes(ext));
      const extOk = ALLOWED_EXT.includes(ext);
      if (!mimeOk || !extOk) {
        setError(`"${file.name}" is not a supported type. Only PDF, JPG, and PNG are allowed.`);
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        setError(`"${file.name}" exceeds the 10 MB limit.`);
        return;
      }
      if (file.size === 0) {
        setError(`"${file.name}" is empty.`);
        return;
      }
      accepted.push({ id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`, file });
    }
    setFiles((prev) => [...prev, ...accepted]);
  }

  function removeFile(id) {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }

  function onDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    addFiles(e.dataTransfer?.files);
  }

  function onDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  function validate() {
    if (!form.name.trim()) return "Please enter your name.";
    if (!form.mobile.trim()) return "Please enter your mobile number.";
    if (!form.email.trim()) return "Please enter your email address.";
    if (!form.subject.trim()) return "Please enter a subject.";
    if (!form.issue_type) return "Please select an issue type.";
    if (!form.description.trim()) return "Please describe your issue.";
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    const v = validate();
    if (v) { setError(v); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, val]) => fd.append(k, val));
      files.forEach(({ file }) => fd.append("attachments", file, file.name));
      const { data } = await api.post("/support/tickets", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setConfirmation(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  // -------------- Confirmation --------------
  if (confirmation) {
    return (
      <ConfirmationScreen
        variant={variant}
        ticket={confirmation}
        mobile={form.mobile}
        onNewTicket={() => {
          setConfirmation(null);
          setFiles([]);
          setForm({ name: user ? form.name : "", mobile: user ? form.mobile : "",
                    email: user ? form.email : "", subject: "", issue_type: "", description: "" });
        }}
      />
    );
  }

  // -------------- Form --------------
  const FormCard = (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="support-form">
      <div>
        <Label htmlFor="sup-name" className="text-sm font-medium text-[#1F2937]">{t("Name")}</Label>
        <Input id="sup-name" data-testid="support-name-input" value={form.name}
          onChange={(e) => set("name", e.target.value)} placeholder="Ahmed Al-Harbi"
          className="mt-1.5 h-11 bg-white border-[#E5E7EB] focus-visible:ring-[#0B7E8C]" />
      </div>

      <div>
        <Label htmlFor="sup-mobile" className="text-sm font-medium text-[#1F2937]">{t("Mobile Number")}</Label>
        <Input id="sup-mobile" data-testid="support-mobile-input" value={form.mobile}
          onChange={(e) => set("mobile", e.target.value)} placeholder="+966 50 123 4567"
          className="mt-1.5 h-11 bg-white border-[#E5E7EB] focus-visible:ring-[#0B7E8C]" />
      </div>

      <div>
        <Label htmlFor="sup-email" className="text-sm font-medium text-[#1F2937]">{t("Email Address")}</Label>
        <Input id="sup-email" data-testid="support-email-input" type="email" value={form.email}
          onChange={(e) => set("email", e.target.value)} placeholder="ahmed.alharbi@email.com"
          className="mt-1.5 h-11 bg-white border-[#E5E7EB] focus-visible:ring-[#0B7E8C]" />
      </div>

      <div>
        <Label htmlFor="sup-subject" className="text-sm font-medium text-[#1F2937]">
          {t("Subject")}<span className="text-[#EF4444]"> *</span>
        </Label>
        <Input id="sup-subject" data-testid="support-subject-input" value={form.subject}
          onChange={(e) => set("subject", e.target.value)} placeholder={t("Brief description of your issue")}
          className="mt-1.5 h-11 bg-white border-[#E5E7EB] focus-visible:ring-[#0B7E8C]" />
      </div>

      <div>
        <Label className="text-sm font-medium text-[#1F2937]">
          {t("Issue Type")}<span className="text-[#EF4444]"> *</span>
        </Label>
        <Select value={form.issue_type} onValueChange={(v) => set("issue_type", v)}>
          <SelectTrigger data-testid="support-issue-type-trigger"
            className="mt-1.5 h-11 bg-white border-[#E5E7EB] focus:ring-[#0B7E8C]">
            <SelectValue placeholder={t("Select an issue type")} />
          </SelectTrigger>
          <SelectContent>
            {ISSUE_TYPES.map((it) => (
              <SelectItem key={it.value} value={it.value} data-testid={`support-issue-type-${it.value}`}>
                {t(it.label)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="sup-desc" className="text-sm font-medium text-[#1F2937]">
          {t("Description")}<span className="text-[#EF4444]"> *</span>
        </Label>
        <Textarea id="sup-desc" data-testid="support-description-input" rows={5} value={form.description}
          onChange={(e) => set("description", e.target.value)}
          placeholder={t("Describe your issue in detail — the more you share, the faster we can help…")}
          className="mt-1.5 bg-white border-[#E5E7EB] focus-visible:ring-[#0B7E8C]" />
      </div>

      {/* Attachments */}
      <div>
        <Label className="text-sm font-medium text-[#1F2937]">
          {t("Attachments")}{" "}
          <span className="text-xs font-normal text-[#6B7280]">
            ({t("optional")} · PDF, JPG, PNG · {t("max")} 10 MB · {t("up to")} {MAX_FILES})
          </span>
        </Label>

        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          data-testid="support-attachment-dropzone"
          className="mt-1.5 flex items-center justify-center gap-3 px-4 py-4 rounded-xl border-2 border-dashed border-[#CBD5E1] bg-[#F9FAFB] hover:border-[#0B7E8C] hover:bg-[#EAF8FA]/40 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click(); }}
        >
          <Paperclip className="w-5 h-5 text-[#0B7E8C]" />
          <div className="text-sm text-[#374151]">
            <span className="font-medium text-[#0B7E8C]">{t("Click to upload")}</span>{" "}
            {t("or drag and drop screenshots / PDFs here")}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            className="hidden"
            data-testid="support-attachment-input"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          />
        </div>

        {files.length > 0 && (
          <ul className="mt-3 space-y-2" data-testid="support-attachment-list">
            {files.map(({ id, file }) => {
              const Icon = fileIcon(file);
              return (
                <li key={id}
                    data-testid="support-attachment-chip"
                    className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-[#E5E7EB]">
                  <Icon className="w-4 h-4 text-[#0B7E8C] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#1F2937] truncate">{file.name}</div>
                    <div className="text-[11px] text-[#6B7280]">{humanSize(file.size)}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(id)}
                    data-testid="support-attachment-remove"
                    aria-label={t("Remove attachment")}
                    className="p-1 rounded-md text-[#6B7280] hover:text-[#EF4444] hover:bg-[#FEF2F2]"
                  >
                    <XIcon className="w-4 h-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {error && (
        <div data-testid="support-error" className="text-sm bg-[#EF444412] border border-[#EF444433] text-[#B91C1C] rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      <Button
        type="submit"
        disabled={submitting}
        data-testid="support-submit-button"
        className="w-full h-12 bg-[#0B7E8C] hover:bg-[#128C97] text-white font-medium text-[15px]"
      >
        {submitting ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("Submitting…")}</>
        ) : (
          <><Send className="w-4 h-4 mr-2" /> {t("Submit Ticket")}</>
        )}
      </Button>
    </form>
  );

  if (variant === "public") {
    return <PublicShell t={t} navigate={navigate}>{FormCard}</PublicShell>;
  }
  return <AppShell t={t} navigate={navigate}>{FormCard}</AppShell>;
}

// ---------- Shells ----------
function PublicShell({ children, t, navigate }) {
  return (
    <div data-testid="support-page-public" className="min-h-screen bg-[#F4F6F8]">
      <header className="bg-[#0B7E8C] text-white">
        <div className="max-w-2xl mx-auto flex items-center justify-between px-5 py-4">
          <button onClick={() => navigate("/login")} data-testid="support-back-to-login"
            className="flex items-center gap-2 text-white/90 hover:text-white text-sm">
            <ArrowLeft className="w-4 h-4" /> {t("Back to sign in")}
          </button>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-white" strokeWidth={2.2} />
            </div>
            <div className="text-sm font-semibold leading-tight">{t("Hospital at Home")}</div>
          </div>
          <LocaleThemeSwitch variant="header" />
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-5 py-8">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white shadow-sm ring-1 ring-[#E5E7EB] mb-3">
            <LifeBuoy className="w-7 h-7 text-[#0B7E8C]" />
          </div>
          <h1 className="text-2xl font-semibold text-[#1F2937]">{t("How can we help?")}</h1>
          <p className="text-sm text-[#6B7280] mt-1">
            {t("Tell us what's happening and we'll be in touch soon.")}
          </p>
        </div>
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-[#E5E7EB] p-6">
          {children}
        </div>
      </main>
    </div>
  );
}

function AppShell({ children, t }) {
  return (
    <div data-testid="support-page-app" className="max-w-2xl mx-auto py-6">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-[#EAF8FA] flex items-center justify-center">
            <LifeBuoy className="w-5 h-5 text-[#0B7E8C]" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-[#1F2937]">{t("Contact Support")}</h1>
            <p className="text-sm text-[#6B7280]">{t("Raise a ticket and our team will get back to you.")}</p>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-[#E5E7EB] p-6">
        {children}
      </div>
    </div>
  );
}

// ---------- Confirmation ----------
function ConfirmationScreen({ ticket, mobile, variant, onNewTicket }) {
  const t = useT();
  const navigate = useNavigate();
  const Wrap = variant === "public" ? PublicShell : AppShell;

  return (
    <Wrap t={t} navigate={navigate}>
      <div data-testid="support-confirmation" className="text-center py-4">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#DCFCE7] mb-4">
          <CheckCircle2 className="w-9 h-9 text-[#15803D]" />
        </div>
        <h2 className="text-2xl font-semibold text-[#1F2937]">{t("Ticket submitted")}</h2>
        <p className="text-sm text-[#6B7280] mt-2 max-w-md mx-auto">
          {t("Thank you — we've received your request. A confirmation SMS has been sent to")}{" "}
          <span className="font-medium text-[#1F2937]">{mobile}</span>.
          {ticket.attachment_count > 0 && (
            <span className="block mt-1 text-[#0B7E8C]" data-testid="support-confirmation-attachments">
              {ticket.attachment_count} {ticket.attachment_count === 1 ? t("attachment") : t("attachments")} {t("uploaded")}.
            </span>
          )}
        </p>

        <div className="mt-6 inline-block bg-[#F4F6F8] border border-[#E5E7EB] rounded-xl px-5 py-4">
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">{t("Your ticket number")}</div>
          <div data-testid="support-ticket-number" className="text-2xl font-mono font-semibold text-[#0B7E8C] mt-1">
            {ticket.ticket_number}
          </div>
        </div>

        <p className="text-xs text-[#6B7280] mt-4 max-w-md mx-auto">
          {t("Please keep this number for reference. Our support team will contact you shortly.")}
        </p>

        <div className="mt-8 flex items-center justify-center gap-3">
          <Button variant="outline" onClick={onNewTicket} data-testid="support-new-ticket-button"
            className="border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#EAF8FA]">
            {t("Submit another ticket")}
          </Button>
          {variant === "public" ? (
            <Link to="/login">
              <Button data-testid="support-back-to-login-cta" className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">
                {t("Back to sign in")}
              </Button>
            </Link>
          ) : (
            <Button onClick={() => navigate("/")} data-testid="support-back-home-cta"
              className="bg-[#0B7E8C] hover:bg-[#128C97] text-white">
              {t("Back to dashboard")}
            </Button>
          )}
        </div>
      </div>
    </Wrap>
  );
}
